import { PublicClientApplication } from "@azure/msal-browser";

// MSAL Configuration - ScaleCapacity Azure AD App
const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
const tenantId = process.env.REACT_APP_AZURE_TENANT_ID;

if (!clientId) {
  throw new Error(
    "REACT_APP_AZURE_CLIENT_ID is required. Please set it in your .env file."
  );
}

if (!tenantId) {
  throw new Error(
    "REACT_APP_AZURE_TENANT_ID is required. Please set it in your .env file."
  );
}

export const msalConfig = {
  auth: {
    clientId,
    // Use your tenant ID - this is critical for proper redirect
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri:
      process.env.REACT_APP_AZURE_REDIRECT_URI ||
      window.location.origin + "/auth/outlook/callback",
    postLogoutRedirectUri:
      process.env.REACT_APP_AZURE_POST_LOGOUT_REDIRECT_URI ||
      window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: process.env.REACT_APP_AZURE_SCOPES
    ? process.env.REACT_APP_AZURE_SCOPES.split(",").map((s) => s.trim())
    : [
        "openid",
        "profile",
        "email",
        "User.Read",
        "Mail.Send",
        "offline_access",
      ],
};

// Create MSAL instance
let msalInstance;

try {
  msalInstance = new PublicClientApplication(msalConfig);

  msalInstance
    .initialize()
    .then(() => {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
      console.log("✅ MSAL initialized successfully");
    })
    .catch((initError) => {
      console.error("❌ MSAL initialization error:", initError);
    });
} catch (configError) {
  console.error("❌ MSAL configuration error:", configError);
  throw configError;
}

export const handlePendingInteraction = async () => {
  if (!msalInstance) return null;

  try {
    const response = await msalInstance.handleRedirectPromise();
    return response;
  } catch (error) {
    console.warn("Error handling redirect promise:", error);
    return null;
  }
};

export const clearInteractionState = () => {
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      if (key.includes("msal") || key.includes("interaction")) {
        sessionStorage.removeItem(key);
      }
    });
    console.log("✅ Cleared MSAL interaction state");
  } catch (error) {
    console.warn("Error clearing interaction state:", error);
  }
};

export const startLoginSafely = async (loginRequest) => {
  if (!msalInstance) {
    throw new Error("MSAL instance not initialized");
  }

  try {
    const redirectResponse = await msalInstance.handleRedirectPromise();
    if (redirectResponse) {
      return { pending: true, response: redirectResponse };
    }
  } catch (error) {
    console.warn("Error handling redirect promise:", error);
    clearInteractionState();
  }

  const interactionKeys = Object.keys(sessionStorage).filter((key) =>
    key.includes("msal.interaction")
  );
  if (interactionKeys.length > 0) {
    console.warn("Found stuck interaction state, clearing...");
    clearInteractionState();
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  try {
    await msalInstance.loginRedirect(loginRequest);
    return { success: true };
  } catch (error) {
    if (error.errorCode === "interaction_in_progress") {
      console.warn("Interaction in progress, clearing and retrying...");
      clearInteractionState();
      await new Promise((resolve) => setTimeout(resolve, 200));
      await msalInstance.loginRedirect(loginRequest);
      return { success: true };
    }
    throw error;
  }
};

export default msalInstance;
